import type {
  MarketingRouterRule as ContractMarketingRouterRule,
  MarketingRouterMetricResult,
  MarketingRouterVariant,
} from '@luxuryescapes/contract-svc-promo/marketing-router/admin';

export type MarketingRouterRuleVariant = MarketingRouterVariant;

/**
 * The metrics a rule can be judged on, exactly as svc-promo's significance
 * endpoint enumerates them. The contract types a saved rule's
 * `significance_metrics` as `string[]`, so this is the narrowing the admin
 * applies on read and write.
 */
export type MarketingRouterRuleSignificanceMetric = MarketingRouterMetricResult['metric'];

/**
 * Runtime form of that union, for type guards and option lists. `satisfies`
 * rejects a key the contract does not know, and the assertion below fails
 * typecheck when the contract adds a metric this list has not caught up with.
 */
export const MARKETING_ROUTER_SIGNIFICANCE_METRICS = [
  'ttv',
  'purchase',
  'margin',
  'margin_ar',
  'margin_after_discount',
  'margin_ar_after_discount',
] as const satisfies ReadonlyArray<MarketingRouterRuleSignificanceMetric>;

type AssertNever<T extends never> = T;
type _EveryContractMetricIsListed = AssertNever<
  Exclude<MarketingRouterRuleSignificanceMetric, (typeof MARKETING_ROUTER_SIGNIFICANCE_METRICS)[number]>
>;

type MarketingRouterRuleMatch = Required<
  Pick<ContractMarketingRouterRule['match'], 'utm_source' | 'utm_medium' | 'utm_campaign'>
> &
  Omit<ContractMarketingRouterRule['match'], 'utm_source' | 'utm_medium' | 'utm_campaign' | 'regions'> & {
    /**
     * Widened from the contract's region-code union: form state carries the
     * operator's raw tags, and svc-promo validates the codes on save.
     */
    regions?: Array<string>;
  };

export type MarketingRouterRule = Omit<ContractMarketingRouterRule, 'significance_metrics' | 'match'> & {
  significance_metrics: Array<MarketingRouterRuleSignificanceMetric>;
  match: MarketingRouterRuleMatch;
};

export function getMarketingRouterRuleCookieName(marketingRouterId: string, variantId: string): string {
  return `${marketingRouterId}_${variantId}`;
}
